/**
 * RTP Venues Management Page
 * Main component for managing RTP Venues with VividSeats map integration
 * Features:
 * - Venue grid display with filtering capabilities
 * - Bulk selection and status management (whitelist/blacklist)
 * - Row click opens drawer with VividSeats venue map
 * - Sync functionality for updating venue data
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
    Chip,
    Box,
    Drawer
} from '@mui/material';
import {
    DatagridConfigurable,
    TextField,
    NumberField,
    FunctionField,
    Identifier
} from 'react-admin';
import RaList from '../../../components/ra-list/RaList';
import RenderControls from '../../../components/ra-list/RenderControls';
import { formatLocalTime } from '../../../components/ex_dayjs';
import { getVenueFilterControls, getDefaultVenueFilters } from './components/VenueFilters';
import VenueMapDrawer from './components/VenueMapDrawer';
import { autoLoad } from '../../../utils/common';
import VerificationActions from '../components/VerificationActions';
import { RTPVenue, DefaultVenueFilters } from './types';

/**
 * Row click handler function type definition
 */
type RowClickHandler = (
    id: Identifier,
    resource: string,
    record: RTPVenue,
    event?: React.MouseEvent<HTMLElement>
) => string | false | void;

/**
 * RTP Venues Management Component
 * Displays venues in a grid with filtering, bulk actions, and map drawer
 * @returns {JSX.Element} The RTP Venues management interface
 */
const RTP_Venues: React.FC = () => {
    const refbtnFilter = useRef<HTMLButtonElement>(null);
    
    // State management
    const [defaultSort] = useState<string[]>(['id asc']);
    const [defaultValues] = useState<DefaultVenueFilters>(getDefaultVenueFilters());
    const [selectedVenue, setSelectedVenue] = useState<RTPVenue | null>(null);
    const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
    const [refreshKey] = useState<number>(0);

    /**
     * Reloads the main venue list by triggering the filter button click
     * This function uses the autoLoad utility to programmatically trigger
     * the filter button, causing the venue list to refresh with current filter criteria
     * @returns {void}
     */
    const reloadMainPage = (): void => {
        autoLoad(refbtnFilter);
    };

    /**
     * Effect hook to automatically trigger initial data load on component mount
     * Ensures the venue list is populated when the component first renders
     */
    useEffect(() => {
        reloadMainPage();
    }, []);

    /**
     * Handles row click to open venue map drawer
     * Returns path for routing-based drawer opening following EmailList pattern
     * Prevents drawer opening when checkbox is clicked to allow proper bulk selection
     * @param {Identifier} id - Venue ID
     * @param {string} resource - Resource name
     * @param {RTPVenue} record - Venue record data
     * @param {React.MouseEvent<HTMLElement>} event - The click event
     * @returns {string | false | void} Navigation path or false to prevent navigation
     */
    const handleRowClick: RowClickHandler = useCallback((id, resource, record, event) => {
        // Check if the click was on a checkbox or checkbox container
        const target = event?.target as HTMLElement;
        if (target && (
            (target as HTMLInputElement).type === 'checkbox' ||
            target.closest('.RaDatagrid-checkbox') ||
            target.closest('[role="checkbox"]') ||
            target.closest('input[type="checkbox"]')
        )) {
            // Don't open drawer when clicking checkbox - allow selection to work
            return;
        }
        
        console.log('Row clicked:', { id, resource, record });
        setSelectedVenue(record);
        setDrawerOpen(true);
        return false; // Prevent default navigation, use our own drawer
    }, []);

    /**
     * Handles drawer close
     */
    const handleDrawerClose = useCallback((): void => {
        setDrawerOpen(false);
        setSelectedVenue(null);
    }, []);

    /**
     * Renders the status chip for a venue record
     * @param {RTPVenue} record - The venue record
     * @returns {JSX.Element} Status chip component
     */
    const renderStatusChip = (record: RTPVenue): JSX.Element => (
        <Chip
            label={record.status === 1 ? 'Active' : 'Inactive'}
            color={record.status === 1 ? 'success' : 'error'}
            size="small"
            variant={record.status === 1 ? 'filled' : 'outlined'}
        />
    );

    /**
     * Renders the verification status chip for a venue record
     * @param {RTPVenue} record - The venue record
     * @returns {JSX.Element} Verification status chip component
     */
    const renderVerificationStatusChip = (record: RTPVenue): JSX.Element => {
        const statusMap: Record<number, string> = { 0: 'Not Verified', 1: 'Whitelist', 2: 'Blacklist' };
        const colorMap: Record<number, 'default' | 'success' | 'error'> = { 0: 'default', 1: 'success', 2: 'error' };
        const status = record.verification_status || 0;
        return (
            <Chip
                label={statusMap[status]}
                color={colorMap[status]}
                size="small"
            />
        );
    };

    /**
     * Renders the layout type chip for a venue record
     * @param {RTPVenue} record - The venue record
     * @returns {JSX.Element | string} Layout type chip or "Not Set" text
     */
    const renderLayoutTypeChip = (record: RTPVenue): JSX.Element | string => {
        if (!record.layout_type) {
            return 'Not Set';
        }
        
        // Capitalize first letter for display
        const displayValue = record.layout_type.charAt(0).toUpperCase() + record.layout_type.slice(1);
        // Define colors for each layout type
        const colorMap: Record<string, string> = {
            standard: '#2e7d32', // Green
            horseshoe: '#9c27b0', // Purple
            table: '#1976d2', // Blue
            other: '#ed6c02' // Orange
        };

        return (
            <Chip
                label={displayValue}
                style={{ backgroundColor: colorMap[record.layout_type] || '#ccc', color: '#fff' }}
                variant="outlined"
                size="small"
            />
        );
    };

    /**
     * Renders the created date for a venue record
     * @param {RTPVenue} record - The venue record
     * @returns {string} Formatted created date
     */
    const renderCreatedDate = (record: RTPVenue): any =>
        record.created ? formatLocalTime(record.created) : '';

    /**
     * Renders the modified date for a venue record
     * @param {RTPVenue} record - The venue record
     * @returns {string} Formatted modified date
     */
    const renderModifiedDate = (record: RTPVenue): any =>
        record.modified ? formatLocalTime(record.modified) : '';

    // Filter controls configuration
    const controls = getVenueFilterControls();

    return (
        <Box display="flex">
            <RaList
                key={refreshKey}
                sortby={defaultSort}
                perPage={25}
                title={'RTP Venues Management'}
                storeKey={true} // Enable selection storage like listings-mapping
                disableSyncWithLocation={true}
                sx={{
                    flexGrow: 1,
                    transition: (theme) =>
                        theme.transitions.create(['all'], {
                            duration: theme.transitions.duration.enteringScreen,
                        }),
                    marginRight: drawerOpen ? '800px' : 0,
                }}
                filters={
                    <RenderControls
                        controls={controls}
                        refFilter={refbtnFilter}
                        defaultValues={defaultValues}
                        exportFile={{
                            filename: `RTP-Venues-${formatLocalTime(new Date())}`,
                            columns: ['id', 'name', 'city', 'state', 'status', 'verification_status', 'layout_type', 'created', 'modified'],
                            limit: 1000000
                        }}
                    />
                }
            >
                <DatagridConfigurable
                    rowClick={handleRowClick}
                    sx={{
                        '& .RaDatagrid-headerCell': {
                            backgroundColor: '#f8f9fa'
                        },
                        '& .RaDatagrid-rowCell': {
                            borderBottom: '1px solid #e0e0e0'
                        }
                    }}
                    bulkActionButtons={<VerificationActions display={true} reload={reloadMainPage} action='UpdateVenues' entityType="Venues" />}
                >
                    <NumberField source="id" label="ID" />
                        
                    <TextField
                        source="name"
                        label="Name"
                        sx={{
                            maxWidth: 300,
                            '& .MuiTableCell-root': {
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }
                        }}
                    />
                        
                    <TextField source="city" label="City" />
                    <TextField source="state" label="State" />
                        
                    <FunctionField
                        source="status"
                        label="Status"
                        render={renderStatusChip}
                    />

                    <FunctionField
                        source="verification_status"
                        label="Verification"
                        render={renderVerificationStatusChip}
                    />

                    <FunctionField
                        source="layout_type"
                        label="Layout Type"
                        render={renderLayoutTypeChip}
                    />
                        
                    <FunctionField
                        source="created"
                        label="Created"
                        render={renderCreatedDate}
                    />
                        
                    <FunctionField
                        source="modified"
                        label="Updated"
                        render={renderModifiedDate}
                    />
                </DatagridConfigurable>
            </RaList>

            {/* Venue Map Drawer - following EmailList pattern */}
            <Drawer
                variant="persistent"
                open={drawerOpen}
                anchor="right"
                onClose={handleDrawerClose}
                sx={{
                    zIndex: 100,
                    '& .MuiDrawer-paper': {
                        width: 800,
                        padding: 2
                    }
                }}
            >
                <VenueMapDrawer
                    open={drawerOpen}
                    onClose={handleDrawerClose}
                    venue={selectedVenue}
                />
            </Drawer>
        </Box>
    );
};

/**
 * RTP Venues Page configuration object
 * Follows the pattern expected by the React Admin resource system
 */
interface RtpVenuesPageConfig {
    name: string;
    list: React.FC;
    options: {
        label: string;
    };
}

const RTP_VenuesPage: RtpVenuesPageConfig = {
    name: 'RTP_Venues',
    list: RTP_Venues,
    options: {
        label: 'RTP Venues'
    }
};

export default RTP_VenuesPage;